import React, { useMemo, useState } from 'react';

import styled from '@emotion/styled';
import { useTranslations } from 'next-intl';

import { useCurrentCompletedUser } from '@web/features/user/service';
import { TabButton } from '@web/ui/components';
import { exists } from '@web/utils/exists';

import { useDepositsOrThrow, useUnstakedPersonas } from '../hooks';

import { StakedTokensGrid } from './StakingGrid/StakedTokensGrid';
import { UnstakedTokensGrid } from './StakingGrid/UnstakedTokensGrid';

const TabsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing('0250')};
  padding-bottom: ${({ theme }) => theme.spacing('1500')};
  border-bottom-color: ${({ theme }) => theme.color.outlineVariant};
  border-bottom-width: ${({ theme }) => theme.outline['1']};
  border-bottom-style: solid;
`;

type KPersonasTab = 'STAKED' | 'UNSTAKED';

export const StakingPersonas = () => {
  const [tab, setTab] = useState<KPersonasTab>('STAKED');

  const t = useTranslations();

  const completedUser = useCurrentCompletedUser();

  const { tokenIds: stakedTokenIds } = useDepositsOrThrow();

  const stakedPersonas = useMemo(() => {
    if (!completedUser) return [];

    return stakedTokenIds
      .map((tokenId) =>
        completedUser.user.personas.find((p) => p.tokenId === tokenId),
      )
      .filter(exists);
  }, [completedUser?.user.personas, stakedTokenIds]);

  const { personas: unstakedPersonas } = useUnstakedPersonas();

  return (
    <>
      <TabsContainer>
        <TabButton
          isActive={tab === 'STAKED'}
          label={t('STAKING_PERSONAS_TAB_STAKED')}
          onClick={() => setTab('STAKED')}
          badgeValue={stakedPersonas.length}
        />
        <TabButton
          isActive={tab === 'UNSTAKED'}
          label={t('STAKING_PERSONAS_TAB_UNSTAKED')}
          onClick={() => setTab('UNSTAKED')}
          badgeValue={unstakedPersonas.length}
        />
      </TabsContainer>
      {tab === 'STAKED' && (
        <StakedTokensGrid
          items={stakedPersonas}
          onSwitchTab={() => setTab('UNSTAKED')}
        />
      )}
      {tab === 'UNSTAKED' && (
        <UnstakedTokensGrid
          items={unstakedPersonas}
          hasStakedPersonas={stakedPersonas.length > 0}
        />
      )}
    </>
  );
};
